import { ContextualInfoButton, ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import ContextualInfoCollapse from "@/components/buttons/ContextualInfoCollapse";
import AdaptationTag from "@/components/display/AdaptationTag";
import { getHazardColor, getHazardIcon, getHazardName } from "@/features/branding";
import { HazardPlusTotal, Year } from "@/features/pe_aldd";
import { AnaHazardId } from "@/store/services/facades/types";
import { PeaSummaryResponse } from "@/store/services/facades/types/pea";
import { ADAPTATION_TERM_COLORS, adaptationTermsConfig, AdaptationType } from "@/types/adaptation";
import { Rcp } from "@/types/rcp";
import { useAdaptationPermissions } from "@/utils/hooks/useAdaptationPermissions";
import { formatAbbreviatedValueWithMetricUnit, numCompare } from "@/utils/number";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import classNames from "classnames";
import { useDeferredValue, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useCollectionState } from "../context/CollectionContext";
import { useMetricOptions } from "../hooks/useMetricOptions";
import { CompareResultsMode, PointDetailsPermissions } from "../types";
import { ChartHeader, DeltaValue, Header } from "./charts/common/Text";
import { computeAbsoluteChange, computeMetricMaximaAcrossAllScenarios, computeRelativeChange, getMetricShortLabel, MetricKey, MetricMaxima, unitForMetric } from "./charts/common/utils";
import HazardSelectedYearBreakdown from "./charts/HazardSelectedYearBreakdown";
import { HazardTimelineChart } from "./charts/HazardTimelineChart";
import { MiniHazardSparkline } from "./charts/MiniHazardSparkline";
import CollectionFiltersLabel from "./CollectionFiltersLabel";
import SelectMetricOptions from "./SelectMetricOptions";
type HazardRow = {
  key: string;
  hazard: string;
  label: string;
  icon: React.ReactNode;
  avgDamageMtd: number | null;
  compareAvgDamageMtd: number | null;
  avgFailureMtd: number | null;
  compareAvgFailureMtd: number | null;
  productivityLoss: number | null;
  compareProductivityLoss: number | null;
  ttip: number | null;
  compareTtip: number | null;
};
type HazardBreakdownProps = {
  id: string;
  data: PeaSummaryResponse;
  compareData?: PeaSummaryResponse;
  showBreakdownTable?: boolean;
  translationPrefix?: string;
  translationPrefixCharts?: string;
  permissions?: PointDetailsPermissions;
  compareResultsMode?: CompareResultsMode;
  comparisonType?: AdaptationType;
  /** Replaces the derived tag on the section header and the table's hazard column. */
  tag?: React.ReactNode;
  isLargeSite: boolean;
  showFilters?: boolean;
};
export const HazardBreakdown = ({
  id,
  data,
  compareData,
  showBreakdownTable = true,
  translationPrefixCharts = "portfolios.portfolioLevel.assetRisk",
  translationPrefix = "portfolios.portfolioLevel.hazardBreakdown",
  permissions,
  compareResultsMode = "baseline",
  comparisonType = "enhanced_resilience",
  tag,
  isLargeSite,
  showFilters = false
}: HazardBreakdownProps) => {
  const {
    rcp,
    year,
    hazard,
    setHazard
  } = useCollectionState();
  const {
    t: tHazardBreakdown
  } = useTranslation("tools", {
    keyPrefix: translationPrefix
  });
  const {
    t: tCharts
  } = useTranslation("tools", {
    keyPrefix: translationPrefixCharts
  });
  const {
    t: termsT
  } = useTranslation("terms");
  const deferredYear = useDeferredValue(year);
  const isComparison = compareResultsMode === "compare" && !!compareData;
  const {
    canRunAutoAdaptation
  } = useAdaptationPermissions({
    isLargeSite
  });
  const [normalized, setNormalized] = useState(false);
  const [metric, setMetric] = useState<MetricKey>("avg_damage_mtd");
  const metricMax = useMemo(() => computeMetricMaximaAcrossAllScenarios(data), [data]);
  const hazardRows: HazardRow[] = useMemo(() => {
    const filteredByRcp = data.filter(row => row.climate_scenario === rcp);
    const compareFilteredByRcp = compareData?.filter(row => row.climate_scenario === rcp) ?? [];
    const toPercent = (value: number | null | undefined) => value == null ? null : value * 100;
    return filteredByRcp.map(row => {
      const compareRow = compareFilteredByRcp.find(r => r.hazard === row.hazard);
      const icon = getHazardIcon(row.hazard as AnaHazardId, {
        style: {
          color: getHazardColor(row.hazard as AnaHazardId)
        }
      });
      const label = getHazardName(row.hazard as AnaHazardId);
      return {
        key: row.hazard,
        hazard: row.hazard,
        label,
        icon,
        avgDamageMtd: toPercent(row.avg_damage_mtd[deferredYear]),
        compareAvgDamageMtd: toPercent(compareRow?.avg_damage_mtd[deferredYear]),
        avgFailureMtd: toPercent(row.avg_failure_mtd[deferredYear]),
        compareAvgFailureMtd: toPercent(compareRow?.avg_failure_mtd[deferredYear]),
        productivityLoss: toPercent(row.productivity_loss[deferredYear]),
        compareProductivityLoss: toPercent(compareRow?.productivity_loss[deferredYear]),
        ttip: row.ttip?.[deferredYear] ?? null,
        compareTtip: compareRow?.ttip?.[deferredYear] ?? null
      };
    }).sort((a, b) => a.label.localeCompare(b.label)).filter(r => String(r.hazard) !== "total");
  }, [data, compareData, rcp, deferredYear, compareResultsMode]);

  // Kind of a hack to check if we are on the point level but ok for now
  const isPointLevel = translationPrefixCharts.includes("pointLevel");
  const {
    metricOptions,
    selectedMetric
  } = useMetricOptions({
    metric,
    setMetric,
    showTtip: isPointLevel,
    isPointLevel,
    permissions,
    translationPrefix: translationPrefixCharts
  });

  // Same per-metric copy the asset risk charts use, e.g. why MVAR is not calculated for extreme heat.
  const hazardDataUnavailableMessage = tCharts(`charts.${getMetricShortLabel(metric).toLowerCase()}.unavailableMessage`, tHazardBreakdown("unavailableMessage"));
  if (!metricOptions.length || !selectedMetric) return null;

  // `false` when the org cannot run adaptations, which renders nothing rather than falling back.
  const headerTag = tag ?? (canRunAutoAdaptation && compareResultsMode && <AdaptationTag type={isPointLevel ? comparisonType : compareResultsMode} />);
  const renderDescription = () => {
    // Point-level uses a single flat description string rather than per-mode variants.
    if (isPointLevel) {
      return tHazardBreakdown("description");
    }
    return canRunAutoAdaptation ? <Trans i18nKey={tHazardBreakdown(`description.${compareResultsMode}`)} values={{
      baseline: termsT("adaptationTypes.baseline"),
      enhancedResilience: termsT("adaptationTypes.enhanced_resilience")
    }} components={{
      span: <strong />
    }} data-sentry-element="Trans" data-sentry-component="renderDescription" data-sentry-source-file="HazardBreakdown.tsx" /> : tHazardBreakdown("description.default");
  };
  return <div id={id} className="grid grid-cols-12 gap-0 @container/wrapper" data-sentry-component="HazardBreakdown" data-sentry-source-file="HazardBreakdown.tsx">
      <div className="col-span-12 flex flex-col gap-1">
        <Header title={tHazardBreakdown("title")} tag={headerTag} data-sentry-element="Header" data-sentry-source-file="HazardBreakdown.tsx" />

        <ContextualInfoCollapse data-sentry-element="ContextualInfoCollapse" data-sentry-source-file="HazardBreakdown.tsx">{renderDescription()}</ContextualInfoCollapse>
      </div>

      <div className="col-span-12 mt-4">
        <SelectMetricOptions variant="tabs" metric={metric} setMetric={setMetric} metricOptions={metricOptions} data-sentry-element="SelectMetricOptions" data-sentry-source-file="HazardBreakdown.tsx" />
      </div>

      <div className="col-span-12 grid grid-cols-12 gap-2 rounded-2xl rounded-tl-none border border-gray-100 bg-surface px-2 pb-2 pt-4 shadow-sm @2xl/wrapper:p-4">
        <div className="col-span-12 text-right @4xl/wrapper:col-span-4">
          <CollectionFiltersLabel withIcons data-sentry-element="CollectionFiltersLabel" data-sentry-source-file="HazardBreakdown.tsx" />
        </div>
        <div className="col-span-12 mb-2 @4xl/wrapper:order-first @4xl/wrapper:col-span-8">
          <ChartHeader title={selectedMetric.title} description={selectedMetric.description} contextualInfoButtonId="metrics" data-sentry-element="ChartHeader" data-sentry-source-file="HazardBreakdown.tsx" />
        </div>
        <div className={classNames("col-span-12 overflow-hidden", {
        "@4xl/wrapper:col-span-7 @5xl/wrapper:col-span-8": showFilters
      })}>
          <div className="w-full rounded-xl border border-gray-100 p-2 pt-4 @2xl/wrapper:p-4">
            <div className="h-[420px] @4xl/wrapper:h-[540px]">
              <HazardTimelineChart key={isComparison ? "hazard-timeline-comparison" : "hazard-timeline"} data={data} compareData={isComparison ? compareData : undefined} rcp={rcp} selectedYear={year} hazard={hazard} normalized={normalized} setNormalized={setNormalized} metric={metric} metricLabel={selectedMetric.shortLabel} compareResultsMode={compareResultsMode} comparisonType={comparisonType} isPointLevel={isPointLevel} canRunAutoAdaptation={canRunAutoAdaptation} unavailableMessage={hazardDataUnavailableMessage}
            // Without the filters panel there is no other hazard colour key on screen.
            showHazardLegend={!showFilters} data-sentry-element="HazardTimelineChart" data-sentry-source-file="HazardBreakdown.tsx" />
            </div>
          </div>
        </div>

        {showFilters && <div className="col-span-12 @4xl/wrapper:col-span-5 @5xl/wrapper:col-span-4">
            <div className="flex h-full w-full flex-col justify-between rounded-xl border border-gray-100 p-2 pt-4 @2xl/wrapper:p-4 @4xl/wrapper:h-[540px] @4xl/wrapper:pb-10 @4xl/wrapper:pt-4">
              <div className="mb-2 text-right text-sm text-gray-500 @2xl/wrapper:text-sm @4xl/wrapper:pb-0">
                <CollectionFiltersLabel rcpSize="medium" />
              </div>

              <div className={`grid gap-2 ${isComparison ? "grid-cols-2" : "grid-cols-1"}`}>
                <div>
                  {isComparison && <div style={{
                color: ADAPTATION_TERM_COLORS["baseline"]
              }}>
                      <ContextualInfoButtonWithText id="baselineAdaptation">
                        {termsT("adaptationTypes.baseline")}
                      </ContextualInfoButtonWithText>
                    </div>}
                  <HazardSelectedYearBreakdown data={data} rcp={rcp} selectedYear={year} hazard={hazard} setHazard={setHazard} metricMax={metricMax} normalized={normalized} metric={metric} />
                </div>
                {isComparison && <div>
                    <div style={{
                color: ADAPTATION_TERM_COLORS[comparisonType]
              }}>
                      <ContextualInfoButtonWithText id={adaptationTermsConfig[comparisonType].contextId}>
                        {termsT(`adaptationTypes.${comparisonType}`)}
                      </ContextualInfoButtonWithText>
                    </div>
                    <HazardSelectedYearBreakdown data={compareData} rcp={rcp} selectedYear={year} hazard={hazard} setHazard={setHazard} metricMax={metricMax} normalized={normalized} metric={metric} />
                  </div>}
              </div>
            </div>
          </div>}
      </div>

      {showBreakdownTable && <div className="col-span-12 mt-4">
          {isComparison && <SelectMetricOptions variant="tabs" metric={metric} setMetric={setMetric} metricOptions={metricOptions} />}
          <div className="rounded-2xl rounded-tl-none border border-gray-100 bg-surface px-2 pb-2 pt-4 shadow-sm @2xl/wrapper:p-4">
            <HazardBreakdownTable data={data} hazardRows={hazardRows} rcp={rcp} year={year} hazard={hazard} setHazard={setHazard} selectedYear={deferredYear} metricMax={metricMax} metric={metric} comparisonType={comparisonType} isComparison={isComparison} isPointLevel={isPointLevel} tag={headerTag} />
          </div>
        </div>}
    </div>;
};
const HazardBreakdownTable = ({
  data,
  hazardRows,
  rcp,
  year,
  hazard,
  setHazard,
  selectedYear,
  metricMax,
  metric,
  comparisonType,
  tag,
  isComparison,
  isPointLevel
}: {
  data: PeaSummaryResponse;
  hazardRows: HazardRow[];
  rcp: Rcp;
  year: Year;
  hazard: HazardPlusTotal;
  setHazard: (hazard: HazardPlusTotal) => void;
  selectedYear: Year;
  metricMax: MetricMaxima;
  metric: MetricKey;
  comparisonType: AdaptationType;
  tag?: React.ReactNode;
  isComparison: boolean;
  isPointLevel: boolean;
}) => {
  const {
    t
  } = useTranslation("terms");
  const comparisonColsConfig = {
    baseline: {
      ...adaptationTermsConfig.baseline,
      label: t("adaptationTypes.baseline")
    },
    compare: {
      ...adaptationTermsConfig[comparisonType],
      label: t(`adaptationTypes.${comparisonType}`)
    },
    absoluteChange: {
      ...adaptationTermsConfig.absoluteChange,
      label: t("absoluteChange")
    },
    relativeChange: {
      ...adaptationTermsConfig.relativeChange,
      label: t("relativeChange")
    }
  };

  // all metrics are in % in hazard breakdown
  const formatPercentValue = (value: number | null) => value == null ? "-" : formatAbbreviatedValueWithMetricUnit(value, "%");
  const hazardCol: ColumnsType<HazardRow>[number] = {
    title: <div className="flex flex-col gap-2">
        {tag && <span className="inline-flex">{tag}</span>}
        <span className="font-medium text-gray-400">
          {t(`scenarios.${rcp}.medium`)}, {year}
        </span>
      </div>,
    className: `${isComparison && "border-r border-gray-100"} ${isPointLevel && "text-xs"}`,
    dataIndex: "label",
    key: "hazard",
    sorter: (a, b) => a.label.localeCompare(b.label),
    render: (_, row) => <div className="flex items-center gap-2">
        {row.icon}
        <span className={classNames("text-sm font-medium", "text-gray-900")}>{row.label}</span>
      </div>
  };
  const columns: ColumnsType<HazardRow> = [hazardCol, {
    title: <div className="w-max" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} role="presentation">
          <ContextualInfoButtonWithText id="metrics" rootClassName="[&_span:first-child]:pointer-events-none">
            Avg MVAR
          </ContextualInfoButtonWithText>
        </div>,
    dataIndex: "avgMvar",
    align: "right",
    width: 220,
    sorter: numCompare(r => r.avgDamageMtd),
    render: (_: number | null, row) => <div className="flex items-center justify-end gap-2">
          <span className="tabular-nums">{formatPercentValue(row.avgDamageMtd)}</span>
          <MiniHazardSparkline data={data} hazardId={row.hazard} rcp={rcp} selectedYear={selectedYear} field="avg_damage_mtd" color={getHazardColor(row.hazard as AnaHazardId)} domainMax={metricMax.avgDamageMtd} />
        </div>
  }, {
    title: <div className="w-max" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} role="presentation">
          <ContextualInfoButtonWithText id="metrics" rootClassName="[&_span:first-child]:pointer-events-none">
            Avg FP
          </ContextualInfoButtonWithText>
        </div>,
    dataIndex: "avgFp",
    align: "right",
    width: 220,
    sorter: numCompare(r => r.avgFailureMtd),
    render: (_: number | null, row) => <div className="flex items-center justify-end gap-2">
          <span className="tabular-nums">{formatPercentValue(row.avgFailureMtd)}</span>
          <MiniHazardSparkline data={data} hazardId={row.hazard} rcp={rcp} selectedYear={selectedYear} field="avg_failure_mtd" color={getHazardColor(row.hazard as AnaHazardId)} domainMax={metricMax.avgFailureMtd} />
        </div>
  }, {
    title: <div className="w-max" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} role="presentation">
          <ContextualInfoButtonWithText id="metrics" rootClassName="[&_span:first-child]:pointer-events-none">
            Avg PL
          </ContextualInfoButtonWithText>
        </div>,
    dataIndex: "avgPl",
    align: "right",
    width: 220,
    sorter: numCompare(r => r.productivityLoss),
    render: (_: number | null, row) => <div className="flex items-center justify-end gap-2">
          <span className="tabular-nums">{formatPercentValue(row.productivityLoss)}</span>
          <MiniHazardSparkline data={data} hazardId={row.hazard} rcp={rcp} selectedYear={selectedYear} field="productivity_loss" color={getHazardColor(row.hazard as AnaHazardId)} domainMax={metricMax.productivityLoss} />
        </div>
  }];
  const buildMetricGroup = (title: string, metric: MetricKey, baselineKey: keyof HazardRow, compareKey: keyof HazardRow): ColumnsType<HazardRow>[number] => {
    const unit = unitForMetric(metric);
    const getBaseline = (row: HazardRow) => row[baselineKey] as number | null;
    const getCompare = (row: HazardRow) => row[compareKey] as number | null;
    const formatValue = (value: number | null) => value == null ? "-" : formatAbbreviatedValueWithMetricUnit(value, unit);
    return {
      title,
      className: "border-r border-gray-100",
      children: [{
        title: <span className="inline-flex items-center gap-1" style={{
          color: adaptationTermsConfig.baseline.color
        }}>
              {comparisonColsConfig.baseline.label}
              <ContextualInfoButton className="!text-xs" id={comparisonColsConfig.baseline.contextId} />
            </span>,
        key: `${title}-baseline`,
        dataIndex: baselineKey,
        sorter: numCompare(getBaseline),
        render: (_: unknown, row: HazardRow) => <span className="tabular-nums" style={{
          color: adaptationTermsConfig.baseline.color
        }}>
              {formatValue(getBaseline(row))}
            </span>
      }, {
        title: <span className="inline-flex items-center gap-1" style={{
          color: comparisonColsConfig.compare.color
        }}>
              {comparisonColsConfig.compare.label}
              <ContextualInfoButton className="!text-xs" id={comparisonColsConfig.compare.contextId} />
            </span>,
        key: `${title}-compare`,
        dataIndex: compareKey,
        sorter: numCompare(getCompare),
        render: (_: unknown, row: HazardRow) => <span className="tabular-nums" style={{
          color: ADAPTATION_TERM_COLORS[comparisonType]
        }}>
              {formatValue(getCompare(row))}
            </span>
      }, {
        title: <span className="inline-flex items-center gap-1" style={{
          color: adaptationTermsConfig.absoluteChange.color
        }}>
              {comparisonColsConfig.absoluteChange.label}
              <ContextualInfoButton className="!text-xs" id={comparisonColsConfig.absoluteChange.contextId} />
            </span>,
        key: `${title}-absoluteChange`,
        sorter: numCompare(r => computeAbsoluteChange(getBaseline(r), getCompare(r))),
        render: (_: unknown, row: HazardRow) => <span className="inline italic tabular-nums *:inline" style={{
          color: adaptationTermsConfig.absoluteChange.color
        }}>
              <DeltaValue mode="absoluteChange" metric={metric} baseline={getBaseline(row)} compare={getCompare(row)} />
            </span>
      }, {
        title: <span className="inline-flex items-center gap-1" style={{
          color: adaptationTermsConfig.absoluteChange.color
        }}>
              {comparisonColsConfig.relativeChange.label}
              <ContextualInfoButton className="!text-xs" id={comparisonColsConfig.relativeChange.contextId} />
            </span>,
        key: `${title}-relativeChange`,
        className: "border-r border-gray-100 !min-w-[150px]",
        sorter: numCompare(r => computeRelativeChange(getBaseline(r), getCompare(r), unit)),
        render: (_: unknown, row: HazardRow) => <span className="inline italic tabular-nums *:inline" style={{
          color: adaptationTermsConfig.relativeChange.color
        }}>
              <DeltaValue mode="relativeChange" metric={metric} baseline={getBaseline(row)} compare={getCompare(row)} />
            </span>
      }]
    };
  };
  const metricGroupByKey: Partial<Record<MetricKey, ColumnsType<HazardRow>[number]>> = {
    avg_damage_mtd: buildMetricGroup("MVAR", "avg_damage_mtd", "avgDamageMtd", "compareAvgDamageMtd"),
    productivity_loss: buildMetricGroup("Productivity Loss", "productivity_loss", "productivityLoss", "compareProductivityLoss"),
    avg_failure_mtd: buildMetricGroup("Failure Probability", "avg_failure_mtd", "avgFailureMtd", "compareAvgFailureMtd"),
    ttip: buildMetricGroup("TTIP", "ttip", "ttip", "compareTtip")
  };
  const comparisonColumns: ColumnsType<HazardRow> = [isPointLevel ? {
    ...hazardCol,
    width: 240,
    fixed: "start"
  } : hazardCol, metricGroupByKey[metric] ?? metricGroupByKey.avg_damage_mtd!];
  const handleRowClick = (row: HazardRow) => setHazard(hazard === row.hazard ? "total" : row.hazard as HazardPlusTotal);
  return <Table<HazardRow> dataSource={hazardRows} columns={isComparison ? comparisonColumns : columns} rowKey={r => r.key} onRow={row => ({
    onClick: () => handleRowClick(row)
  })} rowClassName={row => classNames("cursor-pointer", {
    "bg-gray-100": hazard !== "total" && row.hazard === hazard,
    "opacity-30": hazard !== "total" && row.hazard !== hazard
  })} classNames={{
    body: {
      cell: isPointLevel ? "text-xs" : ""
    },
    header: {
      cell: isPointLevel ? "text-xs tracking-wide" : ""
    }
  }} pagination={false} size={isPointLevel ? "small" : "middle"} scroll={{
    x: isPointLevel ? "max-content" : undefined
  }} data-sentry-element="Table" data-sentry-component="HazardBreakdownTable" data-sentry-source-file="HazardBreakdown.tsx" />;
};